import { useSelector } from 'react-redux'
import { userSlice } from '../../../slices/userSlice'
import { LogOut, Settings, UsersRound, ChevronRight, Info } from 'lucide-react'
import { LinkItem } from '../LinkItem'
import { useLogout } from '@wellesley/auth'
import { getRoute, useAppsNavigate } from '@wellesley/router'
import { Button } from '@wellesley/ui'
import { FC, useState } from 'react'
import { useChangeUser } from '../../../hooks/useChangeUser'
import { CurrentUserCard } from '../../CurrentUserCard'
import { getUserRoute } from 'helpers/routes'
import { UserList } from '../UserList'
import { SwitchUserProfile } from '../SwitchUserProfile'

export type UserMenuProps = {
  className?: string
  onClose?: () => void
}

export const UserMenu: FC<UserMenuProps> = ({ className, onClose }) => {
  const { logout } = useLogout()
  const currentUser = useSelector(userSlice.selectors.selectCurrentUser)
  const users = useSelector(userSlice.selectors.selectUsers)
  const { selectedUserId, changeUser } = useChangeUser()
  const navigate = useAppsNavigate()
  const [showSwitchProfile, setShowSwitchProfile] = useState(false)

  const userCount = users?.length || 0
  const showSwitchUserProfile = userCount > 4

  const handleUserSelect = (userId: string) => {
    const user = users?.find(({ id }) => id === userId)

    if (user) {
      changeUser(userId)
    }
    setShowSwitchProfile(false)
    onClose?.()
  }

  const handleCurrentUserClick = () => {
    if (currentUser) {
      navigate(getUserRoute(currentUser))
      onClose?.()
    }
  }

  const handleLogout = () => {
    logout()
    onClose?.()
  }

  if (showSwitchProfile) {
    return (
      <div className={className}>
        <SwitchUserProfile
          users={users}
          selectedUserId={selectedUserId}
          onUserSelect={handleUserSelect}
          onBack={() => setShowSwitchProfile(false)}
        />
      </div>
    )
  }

  const usersToShow = users?.filter(({ id }) => id !== selectedUserId)

  const showUserList = !showSwitchUserProfile && usersToShow?.length

  return (
    <div className={className}>
      <div className="p-s pb-xs gap-xs flex flex-col items-center justify-center border-b">
        <div
          className="flex w-full items-center justify-center"
          role="button"
          data-testid="current-user-card-link"
          onClick={handleCurrentUserClick}
        >
          <CurrentUserCard
            className="gap-xs flex-col"
            avatarClassName="h-16 w-16 2xl:h-16 2xl:w-16 hover:brightness-90"
            userTextClassname="flex-col !m-none [&>*:first-child]:flex [&>*:first-child]:justify-center"
          />
        </div>
      </div>
      {showSwitchUserProfile && (
        <div className="p-xxs">
          <Button
            variant="tertiary"
            data-testid="switch-user-profile"
            onClick={() => setShowSwitchProfile(true)}
            className="rounded-m p-xxs flex w-full items-center justify-between font-medium text-gray-900 hover:bg-gray-100"
          >
            <div className="gap-xxs flex items-center">
              <UsersRound size={20} />
              <span>Switch user profile ({userCount})</span>
            </div>
            <ChevronRight size={16} />
          </Button>
        </div>
      )}
      {!!showUserList && (
        <div className="gap-xxxs p-xxs flex flex-col">
          <UserList
            users={usersToShow}
            selectedUserId={selectedUserId}
            onUserSelect={handleUserSelect}
          />
        </div>
      )}
      <div>
        {(showUserList || showSwitchUserProfile) && (
          <div className="border-b"></div>
        )}

        <LinkItem
          Icon={Settings}
          title="Settings"
          data-testid="user-menu-settings"
          route={getRoute('/settings/:tab?')}
          routeProps={{ tab: 'profile' }}
          onClick={onClose}
        />

        <LinkItem
          Icon={Info}
          title="About server"
          data-testid="user-menu-about"
          route={getRoute('/about')}
          onClick={onClose}
        />

        <div className="border-b"></div>

        <LinkItem
          Icon={LogOut}
          title="Log out"
          data-testid="user-menu-logout"
          onClick={handleLogout}
        />
      </div>
    </div>
  )
}
